import { Component, ElementRef, Inject, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuySellApproval, BuySellPlan, BuySellPlanEmployee, BuySellPlanVerbose } from '@app/modules/time-off/models/buysell.model';
import { BuySellService } from '@app/modules/time-off/services/buy-sell.service';
import { EmployeeSecurityService } from '../../../employee-security/services/employee-security.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { EmployeeLeaveService } from '../../../employee-details/components/employee-leave/services/employee-leave.service';
import { EmployeeCompensationService } from '../../../employee-details/components/employee-compensation/services/employee-compensation.service';
import { EmployeeTimeOffType } from '../../../employee-details/components/employee-leave/models/leave-request.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeService } from '@app/core/services/employee.service';
import { CurrentUserDetails } from '@app/shared/models/employee.model';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';

@Component({
  selector: 'app-employee-buy-sell-request',
  templateUrl: './employee-buy-sell-request.component.html',
  styleUrls: ['./employee-buy-sell-request.component.scss']
})
export class EmployeeBuySellRequestComponent implements OnInit, AfterViewInit {
  @ViewChild('dialogTitle') dialogTitle: ElementRef<HTMLElement>;
  @ViewChild('requestTabContent') requestTabContent: ElementRef<HTMLElement>;
  @ViewChild('reviewersTabContent') reviewersTabContent: ElementRef<HTMLElement>;
  buySellPlanEmpId: string;
  buySellPlanId: string;
  employeeId: string;
  // Replace single isLoading with multiple specific loading states
  loadingBuySellPlan: boolean = false;
  loadingBuySellPlanEmployee: boolean = false;
  loadingBuySellApprovals: boolean = false;
  loadingEmployeeTimeOffType: boolean = false;
  loadingEmployeeCompensation: boolean = false;
  submittingRequest: boolean = false;
  buySellPlan: any;
  buySellPlanEmployee: BuySellPlanEmployee;
  getRequest: any;
  buySellPlanApproval: any;
  loadingCultures: boolean;
  currentCulture: Culture;
  employeeTimeOffType: EmployeeTimeOffType;
  compensation: any;
  hoursInput: number = 0;
  selectedType: string = 'BUY';
  buySellPlanApprovals: BuySellApproval[] = [];
  currentEmployeeDetails: CurrentUserDetails;
  user$: any;
  isReviewer: boolean = false;

  // Add computed property for overall loading state
  get isAnyLoading(): boolean {
    return this.loadingBuySellPlan || 
           this.loadingBuySellPlanEmployee || 
           this.loadingBuySellApprovals || 
           this.loadingEmployeeTimeOffType || 
           this.loadingEmployeeCompensation || 
           this.loadingCultures ||
           this.submittingRequest;
  }

  constructor(
    private buySellService: BuySellService,
    private route: ActivatedRoute, 
    private translate: TranslateService,
    private employeeSecurityService: EmployeeSecurityService,
    private employeeLeaveService: EmployeeLeaveService,
    private employeeCompensationService: EmployeeCompensationService,
    private overlayService: OverlayService,
    private snackbarService: SnackbarService,
    private dialogRef: MatDialogRef<EmployeeBuySellRequestComponent>,
    private oidcAuthService: OidcAuthService,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    this.buySellPlanEmpId = data.buySellPlanEmpId;
    this.employeeId = data.employeeId;
    this.buySellPlan = data.buySellPlan;
    this.user$ = this.oidcAuthService.userProfile;
    this.isReviewer = data.isReviewer;
  }

  ngOnInit(): void {
    this.getCurrentCulture();
  }

  ngAfterViewInit(): void {
    this.dialogTitle?.nativeElement?.focus();
  }

  getCurrentCulture() {
    this.loadingCultures = true;

    this.employeeSecurityService.getCurrentEmployeeCulture()
      .subscribe({
        next: (res) => {
          this.currentCulture = res;
          this.getBuySellPlanEmployee();
          this.loadingCultures = false;
        },
        error: (err) => {
          this.loadingCultures = false;
        }
      });
  }

  getParamFromURL(param: string): string {
    let paramInURL: string;

    this.route.paramMap.subscribe(
      params => paramInURL = params.get(param)
    )

    return paramInURL;
  }

  getBuySellPlanEmployee() {
    this.loadingBuySellPlanEmployee = true;

    this.buySellService.getBuySellPlanEmployee(this.buySellPlanEmpId).subscribe({
      next: (response) => {
        this.buySellPlanEmployee = response;
        this.buySellPlanId = this.buySellPlanEmployee?.plan?.id;
        this.getBuySellApproval();
        this.getEmployeeTimeOffType();

        this.loadingBuySellPlanEmployee = false;
      },
      error: (error) => {
        this.loadingBuySellPlanEmployee = false;
      }
    });
  }

  getBuySellApproval() {
    this.loadingBuySellApprovals = true;

    this.buySellService.getBuySellApprovals(20, 0, `(employee.id = "${this.employeeId}") AND (plan.id = "${this.buySellPlanId}")`, `orderNumber-asc`).subscribe({
      next: (response) => {
        this.loadingBuySellApprovals = false;
        this.buySellPlanApprovals = response.data;
        this.selectedType = this.buySellPlanApprovals.length > 0 ? this.buySellPlanApprovals[0].buySell?.type?.id : 'BUY';
        this.hoursInput = this.buySellPlanApprovals.length > 0 ? this.buySellPlanApprovals[0].buySell?.hours : 0;
      },
      error: (error) => {
        this.loadingBuySellApprovals = false;
      }
    });
  }

  getEmployeeTimeOffType() {
    if (this.buySellPlan && this.buySellPlan?.timeOffType?.id) {
      this.loadingEmployeeTimeOffType = true;

      this.employeeLeaveService.getEmployeeTimeOffTypes(this.employeeId).subscribe({
        next: (res) => {
          this.loadingEmployeeTimeOffType = false;
          this.employeeTimeOffType = res.find(t => t.timeOffType.id === this.buySellPlan?.timeOffType?.id);
          this.getEmployeeCompensation();
          // Handle the response here
        },
        error: (err) => {
          this.loadingEmployeeTimeOffType = false;
        }
      });
    }
  }

  getEmployeeCompensation() {
    this.loadingEmployeeCompensation = true;

    this.employeeCompensationService.getCompensation(this.employeeId)
      .subscribe({
        next: (res) => {
          this.loadingEmployeeCompensation = false;
          this.compensation = res;
        },
        error: (err) => {
          this.loadingEmployeeCompensation = false;
        }
      });
  }

  submit() {
    this.submittingRequest = true;
    this.overlayService.show('SubmittingRequest');

    this.buySellService.submitBuySellRequest(this.buySellPlanEmployee.id, { hours: this.hoursInput, type: this.selectedType })
      .subscribe({
        next: (res) => {
          this.submittingRequest = false;
          this.overlayService.hide();
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.submittingRequest = false;
          this.overlayService.hide();
        }
      });
  }

  close() {
    this.dialogRef.close();
  }

  onTabChange(event: MatTabChangeEvent): void {
    const panels = [this.requestTabContent, this.reviewersTabContent];
    const target = panels[event.index];
    if (target) {
      setTimeout(() => target.nativeElement.focus());
    }
  }

  getDisplayMode(): 'no-request' | 'tabs' {
    const isAvailable = this.buySellPlanEmployee?.status.id === 'AVAILABLE';
    const isNotCurrentUser = this.user$.userId !== this.employeeId;
    
    return isAvailable && isNotCurrentUser ? 'no-request' : 'tabs';
  }

  getRequestTabMode(): 'form' | 'approval-form' | 'status-only' | 'approval-summary' {
    const statusId = this.buySellPlanEmployee?.status.id;
    const isCurrentUser = this.employeeId === this.user$.userId;
    const correctApproval = this.isReviewer !== false ? this.getCorrectApproval() : null;

    if(correctApproval && ['PENDING_APPROVAL', 'PENDING', 'QUEUED'].includes(statusId)) {
      return 'approval-form';
    }

    if (['NA', 'AVAILABLE', 'DENIED', 'COMPLETE'].includes(statusId)) {
      return isCurrentUser ? 'form' : 'approval-summary';
    }
    
    return 'approval-summary';
  }

  shouldShowCalculations(): boolean {
    const requestTabMode = this.getRequestTabMode();
    return requestTabMode === 'form' || requestTabMode === 'approval-form';
  }

  getCorrectApproval(): BuySellApproval | null {
    if (this.buySellPlanApprovals && this.buySellPlanApprovals.length > 0) {
      const userId = this.user$?.userId;
      const approvals = 
        [...this.buySellPlanApprovals]
        .sort((a, b) => (Number(a.orderNumber ?? 0) - Number(b.orderNumber ?? 0)))
        .filter(a => a.reviewer?.id === userId);

      for (let i = 0; i < approvals.length; i++) {
        if(approvals[i].status?.id === 'PENDING_APPROVAL') {
          return approvals[i];
        }
      }

      if (approvals.length > 0) {
        return approvals[approvals.length - 1];
      }
      return null;
    }
    return null;
  }

  getStatusChipClass(): string {
    const statusId = this.buySellPlanEmployee?.status?.id;
    const classMap = {
      'DENIED': 'red-chip',
      'APPROVED': 'green-chip',
      'Complete': 'green-chip',
      'PENDING': 'orange-chip',
      'PENDING_APPROVAL': 'blue-chip',
      'AVAILABLE': 'blue-chip'
    };
    return classMap[statusId] || '';
  }
}